import React, { useCallback, useEffect, useReducer, useState } from 'react';
import {
    Button,
    Form,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { onChangeFunction, UrlEnum } from '../../../shared';
import { FormSelect } from '../../blocks';
import { getCipLabel } from '../../academic-affair-report/program-criteria-form';
import { getOptionLabel } from '../../academic-affair-program/search-academic-programs-form';
import { useHistory } from 'react-router-dom';
import format from 'date-fns/format';
import { academicProgramsService, capitalProjectService } from '../../../services';
import queryString from 'query-string';
import { RadioCheckGroup } from '../../academic-affair-report/components/RadioCheckGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faFile,
    faPaperclip,
    faTimes,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import eventEmitter from "../../../shared/eventEmitter";

export const ProgramRequestEditProgramRequestModal = ({
    isOpen,
    toggle,
    institutions,
    details,
    degreeLevels,
    INITIAL_STATE,
    setDetails,
}) => {
    const locationStatusOptions = [
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
    ];
    const history = useHistory();
    const [onlineDropdownDisabled, setOnlineDropdownDisabled] = useState(false);
    const [locationsToAdd, setLocationsToAdd] = useState([]);
    const [allFieldsFilled, setAllFieldsFilled] = useState(false);
    const reducer = (state, newState) => ({ ...state, ...newState });

    const [userInput, setUserInput] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        INITIAL_STATE
    );
    const [files, setFiles] = useState(null);

    useEffect(() => {
        setUserInput(INITIAL_STATE);
    }, [INITIAL_STATE]);

    const toggleModal = () => {
        toggle();
        setOnlineDropdownDisabled(false);
        setFiles(null);
        setUserInput(INITIAL_STATE);
    };

    const [options, setOptions] = useReducer(reducer, {});
    const fetchOptions = useCallback(() => {
        academicProgramsService.getInstitutions().then(res => {
            setOptions({
                institutions: res?.map(x => {
                    return { value: x.label, label: x.value };
                }),
            });
        });
        academicProgramsService.getProgramStatusTypes().then(res => {
            setOptions({
                programStatusOptions: res?.map(x => {
                    return { value: x, label: x };
                })
            });
        });        
        academicProgramsService.getCips().then(res => {
            setOptions({
                cipCode: res[2],
            });
        });
    }, []);

    const onFileChange = useCallback(e => {
        setFiles(e.target.files);
    }, []);

    useEffect(() => {
        setOptions({ locations: [] });
        if (institutions.label !== undefined) {
            const query = queryString.stringify(
                { institutions: institutions.value },
                {
                    arrayFormat: 'separator',
                    skipEmptyString: true,
                }
            );
            academicProgramsService.getLocations(query).then(res => {
                setOptions({ locations: res?.locations });
            });
        } else if (userInput.institution) {
            const query = queryString.stringify(
                { institutions: userInput.institution.value },
                {
                    arrayFormat: 'separator',
                    skipEmptyString: true,
                }
            );
            academicProgramsService.getLocations(query).then(res => {
                setOptions({ locations: res.locations });
            });
        }
    }, [userInput.institution, institutions]);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    const editProgramRequest = async () => {
        try {
            const postData = {
                programId: details.requestID,
                degreeLevel: userInput.degreeLevel
                    ? userInput.degreeLevel.value
                    : undefined,
                formalName: userInput.programName ? userInput.programName : undefined,
                cipCode: userInput.cipCode ? userInput.cipCode.value : undefined,
                distanceEdApproved: {
                    id: userInput.programDistanceEd,
                },
                distanceEdOnline: {
                    id: userInput.programDistanceEdOnline,
                },
                locationStatusChanges: getLocationStatusChanges([
                    ...locationsToAdd,
                    ...userInput.locations,
                ]),
                comments: userInput.comments ? userInput.comments : undefined,
                creditRequirementMinimum: userInput.creditRequirementMinimum
                    ? Number(userInput.creditRequirementMinimum)
                    : undefined,
                creditRequirementMaximum: userInput.creditRequirementMaximum
                    ? Number(userInput.creditRequirementMaximum)
                    : undefined,
                institutionId: institutions ? institutions.value : undefined,
                credential: {
                    ProgramURL: userInput.programURL ? userInput.programURL : '',
                    Keywords: userInput.keywords ? userInput.keywords : '',
                    ProgramDescription: userInput.description
                        ? userInput.description
                        : '',
                    CredentialName: userInput.programName
                        ? userInput.programName
                        : undefined,
                },
                ProgramStatusId: userInput.programStatus.value,
            };
            Promise.resolve(academicProgramsService.editProgramRequest(postData)).then(
                postRes => {
                    if (files) {
                        Promise.resolve(
                            academicProgramsService.uploadFiles(files, details.requestID)
                        );
                    }
                    eventEmitter.emit('updateSARData')
                    return postRes;
                }
            );
            toggle();
            setUserInput(INITIAL_STATE);
        } catch (error) {}
    };

    const updateLocations = evt => {
        const newLocation = {
            label: evt.locations[0].label,
            value: evt.locations[0].value,
            statusId: 1,
        };
        setLocationsToAdd(prev => [...prev, newLocation]);
    };

    const getLocationStatusChanges = locations => {
        return locations.map(location => {
            return {
                LocationId: location.value,
                LocationName: location.label,
                StatusId: location.statusId,
            };
        });
    };

    useEffect(() => {
        const skipKeys = [
            'endDate',
            'comments',
            'creditRequirementMinimum',
            'creditRequirementMaximum',
            'cipCodeTitle',
            'programId',
        ];
        var anyEmpty = false;
        for (const key in userInput) {
            if (skipKeys.includes(key)) continue;
            if (userInput[key] === null || userInput[key] === '') {
                anyEmpty = true;
            }
        }
        setAllFieldsFilled(!anyEmpty);
    }, [userInput]);

    const closeBtn = (
        <button className="close" onClick={toggleModal}>
            &times;
        </button>
    );

    const rowStyles = {
        container: provided => ({
            ...provided,
            width: 120,
        }),
    };

    const statusChangeOptions = [
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
    ];

    const getSelectOption = val => {
        return statusChangeOptions.find(x => x.value === val);
    };

    const renderApprovedLocationRows = () => {
        let rows = [];
        locationsToAdd.map(location => {
            rows.push(
                <tr key={location.value}>
                    <td>{location.label}</td>
                    <td className="table-actions">
                        <div className="text-success">Pending Addition</div>
                        <div className="text-danger">
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="cursor-pointer ml-2"
                                onClick={() =>
                                    setLocationsToAdd(prev =>
                                        prev.filter(loc => loc.value !== location.value)
                                    )
                                }
                            />
                        </div>
                    </td>
                </tr>
            );
        });
        userInput.locations.map(location => {
            rows.push(
                <tr key={location.value}>
                    <td>{location.label}</td>
                    <td className="table-actions">
                        <Select
                            styles={rowStyles}
                            onChange={evt => {
                                const updatedLocations = userInput.locations.map(loc => {
                                    if (loc.value === location.value) {
                                        return { ...loc, statusId: evt.value };
                                    }
                                    return loc;
                                });
                                setUserInput({ locations: updatedLocations });
                            }}
                            options={statusChangeOptions}
                            value={getSelectOption(location.statusId)}
                            components={{
                                IndicatorSeparator: () => null,
                            }}
                        />
                    </td>
                </tr>
            );
        });
        return rows;
    };

    return (
        <Modal isOpen={isOpen} className="modal-dialog modal-lg">
            <ModalHeader close={closeBtn}>Edit Program Request</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <label>Institution</label>
                        <Input
                            type="text"
                            placeholder="Institution Name"
                            name="cipCode"
                            value={institutions.label}
                            disabled={true}
                        />
                    </FormGroup>
                    {userInput.requestType === '8' && (
                        <>
                            <FormGroup>
                                <label>Merged Program Name</label>
                                <Input
                                    type="text"
                                    placeholder="Program Name"
                                    onChange={evt =>
                                        onChangeFunction.handleChange(evt, setUserInput)
                                    }
                                    name="programName"
                                    value={userInput.programName}
                                />
                            </FormGroup>
                            <FormSelect
                                label="CIP Code"
                                onChange={setUserInput}
                                options={
                                    options.cipCode
                                        ? options.cipCode.map(x => {
                                              return {
                                                  value: x.cipCode,
                                                  label: `${x.cipCode} - ${x.cipName}`,
                                              };
                                          })
                                        : []
                                }
                                placeholder="CIP Code"
                                name="cipCode"
                                value={userInput.cipCode}
                                isMulti={false}
                                getOptionLabel={getCipLabel}
                                isClearable
                                showLoading
                                required
                            />
                            <div className="col-lg-6">
                                <label className="d-block">
                                    Delivery Mode<sup className="text-danger"> *</sup>
                                </label>
                                <RadioCheckGroup
                                    onChange={setUserInput}
                                    name="programDistanceEd"
                                    value={userInput.programDistanceEd}
                                    options={[
                                        {
                                            value: 1,
                                            label: 'On Campus Only',
                                        },
                                        {
                                            value: 2,
                                            label: 'Online Only',
                                        },
                                        {
                                            value: 3,
                                            label: 'On Campus & Online',
                                        },
                                    ]}
                                />
                            </div>
                            {<div className="col-lg-6">
                                <label className="d-block">
                                    Nature of Online Program<sup className="text-danger"> *</sup>
                                </label>
                                <RadioCheckGroup
                                  onChange={setUserInput}
                                  name="programDistanceEdOnline"
                                  value={userInput.programDistanceEdOnline}
                                  options={[
                                      {
                                          value: 0,
                                          label: "None"
                                      },
                                      {
                                          value: 1,
                                          label: '100% Online',
                                      },
                                      {
                                          value: 2,
                                          label: 'Blended',
                                      }
                                  ]}
                                />
                            </div>}

                            <div className="col-lg">
                                <FormGroup>
                                    <label>Credit Requirement Minimum</label>
                                    <Input
                                        type="number"
                                        style={{ textAlign: 'left' }}
                                        placeholder="Minimum Credits"
                                        onChange={evt =>
                                            onChangeFunction.handleChange(
                                                evt,
                                                setUserInput
                                            )
                                        }
                                        name="creditRequirementMinimum"
                                        value={userInput.creditRequirementMinimum}
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg">
                                <FormGroup>
                                    <label>Credit Requirement Maximum</label>
                                    <Input
                                        type="number"
                                        style={{ textAlign: 'left' }}
                                        placeholder="Maximum Credits"
                                        onChange={evt =>
                                            onChangeFunction.handleChange(
                                                evt,
                                                setUserInput
                                            )
                                        }
                                        name="creditRequirementMaximum"
                                        value={userInput.creditRequirementMaximum}
                                    />
                                </FormGroup>
                            </div>
                        </>
                    )}
                    {userInput.requestType !== '8' && (
                        <FormGroup>
                            <label>Program Name</label>
                            <Input
                                type="text"
                                placeholder="Program Name"
                                onChange={evt =>
                                    onChangeFunction.handleChange(evt, setUserInput)
                                }
                                name="programName"
                                disabled={userInput.requestType !== '2' && userInput.requestType !== '1'}
                                value={userInput.programName}
                            />
                        </FormGroup>
                    )}
                    <FormGroup>
                        {(userInput.requestType === '4' ||
                            userInput.requestType === '1') && (
                            <FormSelect
                                label="CIP Code"
                                onChange={setUserInput}
                                options={
                                    options.cipCode
                                        ? options.cipCode.map(x => {
                                              return {
                                                  value: x.cipCode,
                                                  label: `${x.cipCode} - ${x.cipName}`,
                                              };
                                          })
                                        : []
                                }
                                placeholder="CIP Code"
                                name="cipCode"
                                value={userInput.cipCode}
                                isMulti={false}
                                getOptionLabel={getCipLabel}
                                isClearable
                                showLoading
                                required
                            />
                        )}
                    </FormGroup>

                    {(userInput.requestType === '9' || userInput.requestType === '1') && (
                        <FormSelect
                            label="Select Locations To Add"
                            onChange={evt => updateLocations(evt)}
                            options={
                                options.locations
                                    ? options.locations
                                          .filter(loc => {
                                              const alreadySelected = userInput.locations.some(
                                                  selected => selected.value === loc.key
                                              );
                                              const alreadyAdded = locationsToAdd.some(
                                                  added => added.value === loc.key
                                              );
                                              return !alreadySelected && !alreadyAdded;
                                          })
                                          .map(x => {
                                              return { value: x.key, label: x.value };
                                          })
                                    : []
                            }
                            closeAfterSelection={true}
                            placeholder="Select a location to add to the program (if any)"
                            name="locations"
                            value={[]}
                            isMulti
                            isClearable
                            required
                        />
                    )}
                    <div className="form-row align-items-center">
                        {(userInput.requestType === '5' ||
                            userInput.requestType === '1') && (
                              <>
                                <div className="col-lg-6">
                                    <label className="d-block">
                                        Delivery Mode<sup className="text-danger"> *</sup>
                                    </label>
                                    <RadioCheckGroup
                                        onChange={setUserInput}
                                        name="programDistanceEd"
                                        value={userInput.programDistanceEd}
                                        options={[
                                            {
                                                value: 1,
                                                label: 'On Campus Only',
                                            },
                                            {
                                                value: 2,
                                                label: 'Online Only',
                                            },
                                            {
                                                value: 3,
                                                label: 'On Campus & Online',
                                            },
                                        ]}
                                    />
                                </div>
                                {<div className="col-lg-6">
                                        <label className="d-block">
                                        Nature of Online Program<sup className="text-danger"> *</sup>
                                        </label>
                                        <RadioCheckGroup
                                        onChange={setUserInput}
                                        name="programDistanceEdOnline"
                                        value={userInput.programDistanceEdOnline}
                                        options={[
                                            {
                                                value: 0,
                                                label: "None"
                                            },
                                            {
                                                value: 1,
                                                label: '100% Online',
                                            },
                                            {
                                                value: 2,
                                                label: 'Blended',
                                            }
                                        ]}
                                        />
                                </div>}
                              </>
                        )}

                        {(userInput.requestType === '6' ||
                            userInput.requestType === '1') && (
                            <div className="col-lg-6">
                                <FormSelect
                                    label="Program Status"
                                    onChange={setUserInput}
                                    options={options.programStatusOptions}
                                    placeholder="Status"
                                    name="programStatus"
                                    value={userInput.programStatus}
                                    isMulti={false}
                                    isClearable
                                    showLoading
                                    required
                                />
                            </div>
                        )}

                        {(userInput.requestType === '3' ||
                            userInput.requestType === '1') && (
                            <>
                                <div className="col-lg">
                                    <FormGroup>
                                        <label>Credit Requirement Minimum</label>
                                        <Input
                                            type="number"
                                            style={{ textAlign: 'left' }}
                                            placeholder="Minimum Credits"
                                            onChange={evt =>
                                                onChangeFunction.handleChange(
                                                    evt,
                                                    setUserInput
                                                )
                                            }
                                            name="creditRequirementMinimum"
                                            value={userInput.creditRequirementMinimum}
                                        />
                                    </FormGroup>
                                </div>

                                <div className="col-lg">
                                    <FormGroup>
                                        <label>Credit Requirement Maximum</label>
                                        <Input
                                            type="number"
                                            style={{ textAlign: 'left' }}
                                            placeholder="Maximum Credits"
                                            onChange={evt =>
                                                onChangeFunction.handleChange(
                                                    evt,
                                                    setUserInput
                                                )
                                            }
                                            name="creditRequirementMaximum"
                                            value={userInput.creditRequirementMaximum}
                                        />
                                    </FormGroup>
                                </div>
                            </>
                        )}
                    </div>
                    {(userInput.requestType === '9' || userInput.requestType === '1') && (
                        <div>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Location</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>{renderApprovedLocationRows()}</tbody>
                            </table>
                        </div>
                    )}
                    <div className="form-row align-items-center">
                        <div className="col-lg-12">
                            <label>Support Documentation</label>
                        </div>
                    </div>
                    <div className="w-100 hidden-file-input-small">
                        <input
                            type="file"
                            accept=".csv,.zip,.pdf,.xls,.xlxs"
                            onChange={onFileChange}
                            multiple={true}
                        />
                        <button type="button" className="btn btn-outline-primary btn-sm">
                            <i className="far"></i> <FontAwesomeIcon icon={faUpload} />
                            Upload
                        </button>
                    </div>
                    {files &&
                        Array.from(files).map((x, index) => (
                            <h5 key={index} className="mb-0 mt-4 text-italic">
                                <FontAwesomeIcon icon={faPaperclip} /> {x.name}
                            </h5>
                        ))}
                    <FormGroup>
                        <label>
                            Please provide any additional information necessary to process
                            your request.
                        </label>
                        <Input
                            type="textarea"
                            rows="3"
                            placeholder="Place Comments Here"
                            onChange={evt =>
                                onChangeFunction.handleChange(evt, setUserInput)
                            }
                            name="comments"
                            value={userInput.comments}
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggleModal}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={editProgramRequest}
                    disabled={!allFieldsFilled}
                >
                    Submit
                </Button>{' '}
            </ModalFooter>
        </Modal>
    );
};
